/**
 * "Preview as <client>" runtime overlay — ProjectTasks #115 P2.
 *
 * Internal-only: on the demo/dev build, an operator can preview EXACTLY what a
 * prospective client's product looks like (their feature config + their org's
 * data), live, with no separate deploy. This is the runtime selection that
 * overlays the build-time `SURFACE` (productSurface.ts) and `VITE_CLIENT`
 * (clientProducts.ts):
 *
 *   - while a preview is active, `isInternalSurface()` returns false, so every
 *     cockpit gate that already checks it hides (the client never sees dev
 *     chrome) — without editing those gates;
 *   - `activeClientConfig()` resolves to the previewed client's feature config;
 *   - the caller also switches the active demo company to the client's org, so
 *     portfolio/project/timesheet data scopes to them (reuses existing scoping).
 *
 * Pure module store (no React import) so `productSurface` / `clientProducts` can
 * read it without a dependency cycle. Components subscribe via
 * `useSyncExternalStore(subscribePreviewClient, getPreviewClientKey)`.
 * Persisted to localStorage so a refresh keeps the preview.
 */
const STORAGE_KEY = 'tesseract-preview-client'

function readPersisted(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) || null
  } catch {
    return null
  }
}

let previewClientKey: string | null = readPersisted()
/** The demo company that was active before preview began — restored on exit. */
let priorCompanyId: string | null | undefined
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

/** Current previewed client key, or null when not previewing. */
export function getPreviewClientKey(): string | null {
  return previewClientKey
}

/** True while a client preview is active (internal build only). */
export function isPreviewingClient(): boolean {
  return previewClientKey != null
}

/** Begin previewing a client. `priorCompany` is captured so exit can restore it. */
export function startPreviewClient(key: string, priorCompany: string | null | undefined): void {
  priorCompanyId = priorCompany
  previewClientKey = key
  try {
    localStorage.setItem(STORAGE_KEY, key)
  } catch { /* ignore disabled storage */ }
  emit()
}

/** Stop previewing. Returns the company id to restore (captured at start). */
export function stopPreviewClient(): string | null | undefined {
  previewClientKey = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch { /* ignore */ }
  emit()
  const restore = priorCompanyId
  priorCompanyId = undefined
  return restore
}

/** Subscribe to preview changes (for useSyncExternalStore). */
export function subscribePreviewClient(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}
